import { getLocalUserId } from "../utils/getLocalUserId";
import { defaultApi } from "./api";
import { BASE_URL, GET_CATEGORY_URL, GET_AllCATEGORY_URL, IMAGE_BASE_URL } from "./constant";
import { PRODUCT_GET_STATIC_PAGE_URL } from "./constant";
import { getUtmPayload } from "../utils/utmTracker";




function getApiOrigin() {
  try {
    return new URL(BASE_URL).origin;
  } catch (error) {
    return "";
  }
}

function resolveCategoryImageUrl(imagePath) {
  if (!imagePath) {
    return "";
  }

  if (/^https?:\/\//i.test(imagePath)) {
    return imagePath;
  }

  const normalizedPath = imagePath.replace(/^\/+/, "");

  if (normalizedPath.startsWith("uploads/")) {
    return `${IMAGE_BASE_URL}/${normalizedPath.replace(/^uploads\/+/, "")}`;
  }

  return `${IMAGE_BASE_URL}/${normalizedPath}`;
}

function normalizeCategoryItem(category) {
  const apiOrigin = getApiOrigin();
  const resolvedImage = resolveCategoryImageUrl(category.category_image);
  const fileName = category.category_image?.split("/").pop();

  const imageCandidates = [
    resolvedImage,
    fileName ? `${BASE_URL}/uploads/${fileName}` : "",
    fileName && apiOrigin ? `${apiOrigin}/uploads/${fileName}` : "",
  ].filter(Boolean);

  return {
    ...category,
    category_image: imageCandidates[0] || "",
    category_image_candidates: [...new Set(imageCandidates)],
    browse_category_id: category.parent_category_id || category.category_id,
  };
}

function normalizeCategoryList(categoryList = []) {
  return categoryList
    .filter((category) => !category.category_status || category.category_status === "Active")
    .map(normalizeCategoryItem);
}

function extractProductPayload(payload) {
  const responseData = payload?.data;

  if (Array.isArray(responseData)) {
    return responseData;
  }

  if (Array.isArray(responseData?.data)) {
    return responseData.data;
  }

  return responseData ?? null;
}

function normalizeProductListResponse(payload) {
  const extractedPayload = extractProductPayload(payload);
  const data = Array.isArray(extractedPayload)
    ? extractedPayload
    : extractedPayload
      ? [extractedPayload]
      : [];

  return {
    ...payload,
    data,
  };
}

function getCartPayload(payload) {
  if (Array.isArray(payload)) {
    return payload;
  }

  const candidates = [
    payload?.data?.data,
    payload?.data?.products,
    payload?.data?.cart,
    payload?.data,
    payload?.products,
    payload?.cart,
  ];

  return candidates.find((candidate) => candidate !== undefined) || [];
}

function normalizeCartItems(payload) {
  const cartPayload = getCartPayload(payload);
  const items = Array.isArray(cartPayload)
    ? cartPayload
    : cartPayload && typeof cartPayload === "object"
      ? Object.values(cartPayload)
      : [];

  return items
    .filter(Boolean)
    .map((item) => {
      const productId =
        item.product_id ||
        item.productId ||
        item.id ||
        item.product?.product_id ||
        item.product?.id;
      const quantity = Number.parseInt(item.quantity || item.qty || 0, 10);
      const price = Number.parseFloat(
        item.price ||
          item.product_price ||
          item.product?.product_price ||
          item.product_sell_price ||
          item.product?.product_sell_price ||
          0
      );
      const originalPrice = Number.parseFloat(
        item.originalPrice ||
          item.original_price ||
          item.product_sell_price ||
          item.product?.product_sell_price ||
          0
      );
      const discount = Number.parseFloat(
        item.discount || item.product_discount || item.product?.discount || 0
      );

      return {
        ...item,
        id: item.id || item.cart_id || productId,
        cart_id: item.cart_id || item.id,
        product_id: productId,
        quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1,
        price: Number.isFinite(price) ? price : 0,
        originalPrice: Number.isFinite(originalPrice) ? originalPrice : 0,
        discount: Number.isFinite(discount) ? discount : 0,
        product_name:
          item.product_name ||
          item.name ||
          item.title ||
          item.product?.product_name ||
          item.product?.name ||
          "Kuku Product",
        thumbnail_image:
          item.thumbnail_image ||
          item.image ||
          item.product_image ||
          item.product?.thumbnail_image ||
          item.product?.image ||
          "",
        weight:
          item.weight ||
          item.item_weight ||
          item.packSize ||
          item.product?.weight ||
          item.product?.item_weight ||
          "",
      };
    })
    .filter((item) => item.product_id);
}

export function getCartItemsFromResponse(cartData) {
  return Object.values(cartData?.data?.data || {});
}

export function getCartSyncProducts(items = []) {
  return items
    .filter((item) => item?.product_id && Number(item.quantity) > 0)
    .map((item) => ({
      product_id: Number.isNaN(Number(item.product_id))
        ? item.product_id
        : Number(item.product_id),
      quantity: Number(item.quantity),
      price: Number(item.price || item.product_price || item.product_sell_price || 0),
    }));
}

function normalizeSingleProductResponse(payload) {
  const extractedPayload = extractProductPayload(payload);
  const data = Array.isArray(extractedPayload)
    ? extractedPayload[0] || null
    : extractedPayload || null;

  return {
    ...payload,
    data,
  };
}

function isActiveProduct(product) {
  return !product?.product_status || product.product_status === "Active";
}

function matchesFilter(product, filterParameter, filterId) {
  if (filterId === undefined || filterId === null || filterId === "") {
    return true;
  }

  if (!filterParameter || filterParameter === "product" || filterParameter === "products" || filterParameter === "all") {
    return true;
  }

  if (filterParameter === "slug") {
    return product?.slug === filterId;
  }

  if (filterParameter === "category_id") {
    return String(product?.category_id) === String(filterId);
  }

  if (filterParameter === "product_id") {
    return String(product?.product_id) === String(filterId);
  }

  if (filterParameter === "is_featured") {
    return String(product?.is_featured) === String(filterId);
  }

  return String(product?.[filterParameter]) === String(filterId);
}

async function fetchProductsList() {
  const res = await defaultApi.get("/product/getProducts");
  const normalizedResponse = normalizeProductListResponse(res.data);

  return normalizedResponse.data.filter(isActiveProduct);
}

function findProductCartMatch(products = [], productId) {
  for (const product of products) {
    if (String(product?.product_id) === String(productId)) {
      return { product, variant: product };
    }

    const variant = Array.isArray(product?.variant_data)
      ? product.variant_data.find((item) => String(item?.product_id) === String(productId))
      : null;

    if (variant) {
      return { product, variant };
    }
  }

  return { product: null, variant: null };
}

export async function getProducts({ queryKey }) {
  const [filterParameter, filterId] = queryKey || [];
  const products = await fetchProductsList();

  return {
    result: 1,
    msg: "Data Found !!",
    data: products.filter((product) =>
      matchesFilter(product, filterParameter, filterId)
    ),
  };
}

export async function getProductsQuery({ queryKey }) {
  const slug = queryKey?.[1]?.slug;
  const products = await fetchProductsList();
  const product = products.find((item) => item?.slug === slug) || null;

  return normalizeSingleProductResponse({
    result: product ? 1 : 0,
    msg: product ? "Data Found !!" : "Data Not Found !!",
    data: product,
  });
}

export async function getProductBySlug(slug) {
  const products = await fetchProductsList();
  const product = products.find((item) => item?.slug === slug) || null;

  return normalizeSingleProductResponse({
    result: product ? 1 : 0,
    msg: product ? "Data Found !!" : "Data Not Found !!",
    data: product,
  });
}

export async function getProductImages(id) {
  const formData = new FormData();
  formData.append("product_id", id);
  const res = await defaultApi.post(`/api/product/getProductImages`, formData);
  return res.data;
}

export async function addCartMutation(data) {
  const user_id = data?.user_id ?? getLocalUserId();
  const product = data?.data || data?.product || data;
  const cartData = await getCarts({ queryKey: ["get-cart", { user_id }] });
  const currentItems = getCartItemsFromResponse(cartData);
  const itemToAdd = {
    product_id: product?.product_id,
    quantity: Number(data?.quantity || 1),
    price: Number(product?.product_price || product?.price || product?.product_sell_price || 0),
    discount: Number(product?.discount || product?.product_discount || 0),
  };

  const nextItems = currentItems.some(
    (item) => String(item.product_id) === String(itemToAdd.product_id)
  )
    ? currentItems.map((item) =>
        String(item.product_id) === String(itemToAdd.product_id)
          ? { ...item, quantity: Number(item.quantity || 0) + itemToAdd.quantity }
          : item
      )
    : [...currentItems, itemToAdd];

  return syncCartItems({ user_id, items: nextItems });
}

export async function getCarts(args) {
  const { queryKey: { 1: obj } } = args;
  const res = await defaultApi.get(`/cart/get?user_id=${obj.user_id}`);
  const normalizedItems = normalizeCartItems(res.data);
  const a={};
  const products = await fetchProductsList();
  for (const value of normalizedItems) {
    const id = value.product_id;
    const { product, variant } = findProductCartMatch(products, id);

    value.thumbnail_image = product?.thumbnail_image || value.thumbnail_image || "";
    value.product_name = product?.product_name || value.product_name;
    value.price =
      variant?.product_price ||
      product?.product_price ||
      value.price ||
      variant?.price ||
      product?.product_sell_price ||
      0;
    value.originalPrice =
      variant?.product_sell_price ||
      product?.product_sell_price ||
      value.originalPrice ||
      value.original_price ||
      0;
    value.discount =
      value.discount ||
      product?.discount ||
      product?.product_discount ||
      0;
    value.weight =
      variant?.weight ||
      variant?.item_weight ||
      value.weight ||
      product?.weight ||
      product?.item_weight ||
      "";
    a[String(id)]=value;
    
  }
  res.data.data=a;

  return res;
}

export async function syncCart({ user_id, products }) {
  const res = await defaultApi.post("/cart/sync", {
    user_id,
    products: getCartSyncProducts(products),
  });

  return res.data;
}

export async function syncCartItems({ user_id, items }) {
  return syncCart({ user_id, products: items });
}

export async function clearCarts(data) {
  try {
    const user_id = data?.user_id ?? getLocalUserId();
    const formData = new FormData();
    formData.append("user_id", user_id);

    const res = await defaultApi.post("/cart/clear", formData);
    return res.data;
  } catch (e) {
    return e;
  }
}

export async function updateCarts(data) {
  const user_id = data?.user_id ?? getLocalUserId();

  if (Array.isArray(data?.items)) {
    return syncCartItems({ user_id, items: data.items });
  }

  const cartData = await getCarts({ queryKey: ["get-cart", { user_id }] });
  const currentItems = getCartItemsFromResponse(cartData);
  const nextItems = currentItems
    .map((item) =>
      String(item.cart_id || item.id || item.product_id) === String(data?.cart_id || data?.id || data?.product_id)
        ? { ...item, quantity: Number(data?.quantity || item.quantity) }
        : item
    )
    .filter((item) => Number(item.quantity) > 0);

  return syncCartItems({ user_id, items: nextItems });
}

export async function removeCarts({ cart_id }) {
  try {
    const user_id = getLocalUserId();
    const cartData = await getCarts({ queryKey: ["get-cart", { user_id }] });
    const nextItems = getCartItemsFromResponse(cartData).filter(
      (item) =>
        String(item.cart_id || item.id || item.product_id) !== String(cart_id)
    );

    return syncCartItems({ user_id, items: nextItems });
  } catch (e) {
    return e;
  }
}

export async function createOrder(data) {
  try {
    const token = localStorage.getItem("token");
    const payload = {
      ...data,
      ...getUtmPayload(),
    };
    const res = await defaultApi.post('/api/order/create', payload, {
      headers: {
        token: token
      }
    });
    return res.data;
  } catch (e) {
    return e;
  }
}

export async function createOrderPayment(data) {
  try {
    const res = await defaultApi.post('/api/payment-order', data);
    return res.data;
  } catch (e) {
    return e;
  }
}

export async function getParentCategories(page) {
  const pageStr = page ? `?parent_category_id=${page}` : "";
  const res = await defaultApi.get(`${GET_AllCATEGORY_URL}${pageStr}`);
  return normalizeCategoryList(res.data.data);
}

export async function getCategory(page) {
  const pageStr = page ? `?parent_category_id=${page}` : "";
  const res = await defaultApi.get(`${GET_CATEGORY_URL}${pageStr}`);
  return normalizeCategoryList(res.data.data);
}
export async function getCategories() {
  const res = await defaultApi.get(GET_CATEGORY_URL);
  return normalizeCategoryList(res.data.data);
}

export async function getOffers() {
  const res = await defaultApi.get("/product/get-offers");
  const offers = Array.isArray(res.data?.data) ? res.data.data : [];

  return offers.filter((offer) => Number(offer?.is_active) === 1 && !offer?.deleted_at);
}

export async function getProductByCategoryID(id) {
  const products = await fetchProductsList();
  return products.filter((product) => String(product?.category_id) === String(id));
}

export async function getStaticPageData({ queryKey }) {
  // later replace this with slug right now page_id is being passed because slug is not configured in backend
  // const pageStr = queryKey[1] ? `?page_id=${ queryKey[1] }` : "";
  const res = await defaultApi.get(`${PRODUCT_GET_STATIC_PAGE_URL}`);
  return res.data.data;
}

export async function getColor(page) {
  // const pageStr = page ? `?parent_category_id=${page}` : "";
  const res = await defaultApi.get(`/api/product/getColors`);

  return res.data.data;
}
